'use client'

import { Render_Types_Enum, TokenDetailsFragment } from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { useNavBar } from '@components/nav/nav-provider'
import SwitchToggle from '@components/switch-toggle'
import { TokenDisplay } from '@components/token-display'
import { Close } from '@radix-ui/react-dialog'
import {
  TabsContent as RadixTabsContent,
  TabsTrigger as RadixTabsTrigger,
  Tabs,
  TabsContentProps,
  TabsList,
  TabsTriggerProps
} from '@radix-ui/react-tabs'
import { getNonLiveDisplayType } from '@utils/common'
import { marketplaceTokenExportRoute, projectShowRoute, userProfileRoute } from '@utils/routes'
import {
  ArrowRightIcon,
  BodyText,
  CloseIcon,
  HeadlineText,
  ImageIcon,
  ArrowLineLeftIcon,
  ArrowsOutIcon,
  DetailText,
  ExportIcon,
  AttributeIcon
} from 'design-configuration/components'
import { AnimatePresence, motion } from 'motion/react'
import { useRouter } from 'next/navigation'
import { createSerializer, inferParserType, useQueryStates } from 'nuqs'
import { useEffect, useState, useMemo, useCallback } from 'react'
import { useUserDisplay } from '@/hooks/use-user-display'
import { Attributes } from './attributes'
import { Details } from './details'
import { Offers } from './offers'
import { OptionalInterceptLink } from './optional-intercept-link'
import { Provenance } from './provenance'
import {
  PROJECTS_WITHOUT_COLLECTOR_CONFIGURABLE_FIELDS,
  TokenPmpCustomization,
  canEditPmp
} from './token-pmp-customization'
import { useTokenEditPermissions } from '../_hooks/use-token-edit-permissions'
import { SecondaryDetails } from './secondary-details'
import { tokenPageSearchParamsParsers } from '../utils'
import { ReservoirBid } from '@utils/reservoir'
import { getVerticalTitle } from '@utils/project'
import Image from 'next/image'
import { parseAsBoolean } from 'nuqs'
import { TrackedButtonNames, TrackedSelectionNames, trackSelectionMade } from '@utils/analytics'
import { trackButtonClicked } from '@utils/analytics'
import { Button } from '@components/button'
import { useLoginOrConnectWallet } from '@/app/auth/artblocks-auth-provider'
import { useSession } from 'next-auth/react'

type TokenPageProps = {
  token: TokenDetailsFragment
  nextToken: { token_id: string } | null | undefined
  previousToken: { token_id: string } | null | undefined
  modal: boolean
  initialSearchParams: inferParserType<typeof tokenPageSearchParamsParsers>
  initialHighestOffer: ReservoirBid | null | undefined
  hideNavigationArrows?: boolean
}

export function TokenPage({
  token,
  nextToken,
  previousToken,
  modal,
  initialSearchParams,
  initialHighestOffer,
  hideNavigationArrows = false
}: TokenPageProps): React.ReactElement {
  useNavBar({
    border: !modal
  })

  const project = token.project
  const tokenName = `${project.name ?? ''} #${token.invocation}`
  const ownerAddress = token.owner_address
  const { displayName: ownerDisplay } = useUserDisplay({
    name: token.owner?.profile_by_id?.name,
    username: token.owner?.profile_by_id?.username,
    address: ownerAddress
  })

  const [tab, setTab] = useState<'attributes' | 'details' | 'provenance' | 'offers'>('attributes')
  const [customizationParams, setCustomizationParams] = useState<Record<string, string>>({})
  const [supplementalParams, setSupplementalParams] = useState<Record<string, string>>({})
  const tokenHasPmpConfigs =
    token.project.pmp_project_configs?.[0]?.pmp_configs?.length > 0 &&
    !PROJECTS_WITHOUT_COLLECTOR_CONFIGURABLE_FIELDS.includes(token.project.id)

  const [{ infoOpen, isCustomizing, liveView, galleryFrame }, setQueryStates] = useQueryStates({
    ...tokenPageSearchParamsParsers,
    liveView: parseAsBoolean.withDefault(initialSearchParams.liveView ?? false),
    galleryFrame: parseAsBoolean.withDefault(initialSearchParams.galleryFrame ?? false),
    infoOpen: parseAsBoolean.withDefault(initialSearchParams.infoOpen ?? true),
    isCustomizing: parseAsBoolean.withDefault(initialSearchParams.isCustomizing ?? false)
  })

  const router = useRouter()
  const { status } = useSession()
  const { loginOrConnectWallet } = useLoginOrConnectWallet()

  // Get authorized addresses including delegate vaults
  const {
    userAddress: connectedUserAddress,
    authorizedAddresses,
    isLoading: isLoadingPermissions
  } = useTokenEditPermissions(token)

  const isConnected = status === 'authenticated'

  const searchParamsSerializer = createSerializer(tokenPageSearchParamsParsers)
  const prevTokenUrl = previousToken
    ? searchParamsSerializer(`/token/${token.contract_address}/${previousToken?.token_id}`, {
        ...initialSearchParams,
        infoOpen,
        isCustomizing,
        liveView,
        galleryFrame
      })
    : null
  const nextTokenUrl = nextToken
    ? searchParamsSerializer(`/token/${token.contract_address}/${nextToken?.token_id}`, {
        ...initialSearchParams,
        infoOpen,
        isCustomizing,
        liveView,
        galleryFrame
      })
    : null

  const preferredDisplayType = liveView ? Render_Types_Enum.Live : getNonLiveDisplayType(project, token)

  const tokenExportUrl = marketplaceTokenExportRoute(token.contract_address, token.token_id)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return
      }

      const targetElement = event.target as HTMLElement
      if (targetElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetElement.tagName)) {
        return
      }

      if (event.key === 'ArrowLeft' && prevTokenUrl && !hideNavigationArrows) {
        if (modal) {
          router.replace(prevTokenUrl)
        } else {
          router.push(prevTokenUrl)
        }
      } else if (event.key === 'ArrowRight' && nextTokenUrl && !hideNavigationArrows) {
        if (modal) {
          router.replace(nextTokenUrl)
        } else {
          router.push(nextTokenUrl)
        }
      } else if (event.key === '/') {
        setQueryStates({ infoOpen: !infoOpen })
      } else if (event.key === 'e') {
        window.open(tokenExportUrl, '_blank')
      } else if (event.key === 'o') {
        window.open(token.media_url ?? '', '_blank')
      } else if (event.key === 'f') {
        window.open(token.live_view_url ?? '', '_blank')
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [prevTokenUrl, nextTokenUrl, modal, router, infoOpen, tokenExportUrl, setQueryStates, hideNavigationArrows])

  const hasEditPermissions = useMemo(() => {
    if (!isConnected || isLoadingPermissions) return false
    // check if any PMP config is editable by the user with their authorized addresses
    return (
      token.project.pmp_project_configs?.[0]?.pmp_configs?.some((config) =>
        canEditPmp(config, authorizedAddresses, token, connectedUserAddress)
      ) ?? false
    )
  }, [isConnected, isLoadingPermissions, authorizedAddresses, token, connectedUserAddress])

  const handleCustomizationChange = useCallback((key: string, value: string) => {
    setCustomizationParams((prev) => {
      const newParams = { ...prev, [key]: value }
      // remove params with empty values
      if (!value) {
        delete newParams[key]
      }
      return newParams
    })
  }, [])

  const handleSupplementalParamsChange = useCallback((params: Record<string, string>) => {
    setSupplementalParams(params)
  }, [])

  const handleIsCustomizing = useCallback(
    (isCustomizing: boolean) => {
      setQueryStates({ isCustomizing: isCustomizing })
      if (!isCustomizing) {
        setCustomizationParams({})
        setSupplementalParams({})
      }
    },
    [setQueryStates]
  )

  const renderTabContent = () => {
    switch (tab) {
      case 'attributes':
        return <Attributes token={token} key="attributes" />
      case 'details':
        return <Details token={token} key="details" />
      case 'provenance':
        return <Provenance token={token} key="provenance" />
      case 'offers':
        return <Offers token={token} key="offers" />
    }
  }

  return (
    <div
      className={cn('pointer-events-auto md:h-screen md:w-screen', {
        'pt-[var(--nav-bar-height)]': !modal
      })}
    >
      <div className="relative flex flex-col-reverse md:h-full md:w-full md:flex-row">
        {/* Left side bar */}
        <AnimatePresence mode="wait" initial={false}>
          {isCustomizing ? (
            <motion.div
              key="customization"
              className={cn(
                'gap-sm bg-surface left-0 top-0 z-10 flex flex-col [--closed-info-x:0%] md:absolute md:h-full md:w-[500px] md:overflow-hidden md:[--closed-info-x:-100%]'
              )}
              initial={{ x: 'var(--closed-info-x)' }}
              animate={{ x: infoOpen ? 0 : 'var(--closed-info-x)', transition: { visualDuration: 0.4, bounce: 0 } }}
              exit={{ x: 'var(--closed-info-x)', transition: { duration: 0.4, bounce: 0 } }}
            >
              <TokenPmpCustomization
                modal={modal}
                token={token}
                connectedUserAddress={connectedUserAddress}
                authorizedAddresses={authorizedAddresses}
                handleIsCustomizing={handleIsCustomizing}
                onCustomizationChange={handleCustomizationChange}
                onSupplementalParamsChange={handleSupplementalParamsChange}
              />
            </motion.div>
          ) : (
            <motion.div
              key={'info'}
              className={cn(
                'gap-sm bg-surface pt-sm left-0 top-0 z-10 flex flex-col [--closed-info-x:0%] md:absolute md:h-full md:w-[500px] md:overflow-hidden md:[--closed-info-x:-100%]'
              )}
              initial={{ x: 'var(--closed-info-x)' }}
              animate={{ x: infoOpen ? 0 : 'var(--closed-info-x)', transition: { visualDuration: 0.4, bounce: 0 } }}
              exit={{ x: 'var(--closed-info-x)', transition: { duration: 0.4, bounce: 0 } }}
            >
              <div className="px-sm flex flex-col">
                <div className="border-outline-variant rounded-xs p-sm border">
                  <DetailText className="text-on-surface-variant mb-2xs font-medium uppercase">
                    {getVerticalTitle(project)}
                  </DetailText>
                  <HeadlineText className="mb-2xs font-medium">{tokenName}</HeadlineText>
                  <BodyText className="text-on-surface-variant mb-sm">
                    Owned by{' '}
                    <AllPurposeLink
                      href={userProfileRoute(token.owner?.profile_by_id?.username ?? token.owner_address)}
                    >
                      {ownerDisplay}
                    </AllPurposeLink>
                  </BodyText>
                  <BodyText className="hover:underline">
                    <AllPurposeLink
                      href={projectShowRoute(project.slug)}
                      onClick={() => {
                        trackButtonClicked(TrackedButtonNames.openProjectLink, {
                          token,
                          customProperties: {
                            location: 'Token Page'
                          }
                        })
                      }}
                    >
                      <span className="italic">{project.name}</span> by {project.artist_name}
                    </AllPurposeLink>
                  </BodyText>
                  <SecondaryDetails token={token} className="mt-md" initialHighestOffer={initialHighestOffer} />
                </div>
              </div>
              <div className="px-sm">
                <div className="border-outline-variant rounded-xs p-sm gap-xs flex flex-col border">
                  <SwitchToggle
                    leftLabel="Show artwork in gallery frame"
                    switchedRight={galleryFrame}
                    onSwitchChange={(checked) => {
                      trackButtonClicked(TrackedButtonNames.galleryFrame, {
                        token,
                        customProperties: {
                          checked
                        }
                      })
                      setQueryStates({ galleryFrame: checked === true ? checked : false })
                    }}
                    className="w-full justify-between"
                  />
                  <SwitchToggle
                    leftLabel="Enable live rendering"
                    switchedRight={liveView}
                    onSwitchChange={(checked) => {
                      trackButtonClicked(TrackedButtonNames.toggleLiveView, {
                        token,
                        customProperties: {
                          'Toggling off': !checked,
                          location: 'Token Page'
                        }
                      })
                      setQueryStates({ liveView: checked === true ? checked : false })
                    }}
                    className="w-full justify-between"
                  />
                  {tokenHasPmpConfigs ? (
                    <div className="gap-xs flex w-full flex-col">
                      <div className="flex w-full items-center justify-between">
                        <Button
                          onClick={(event) => {
                            event.stopPropagation()
                            if (!isConnected) {
                              loginOrConnectWallet?.()
                            } else {
                              setQueryStates({ isCustomizing: true })
                            }
                          }}
                          colorTheme="outline"
                          className="gap-x-xs flex w-full items-center justify-center"
                          iconLeft={<AttributeIcon className="w-sm h-sm fill-on-primary-inverted" />}
                          disabled={isConnected && !hasEditPermissions}
                        >
                          <BodyText>{isConnected ? 'Customize artwork' : 'Connect wallet to customize'}</BodyText>
                        </Button>
                      </div>
                      {!isConnected && !hasEditPermissions && (
                        <BodyText className="text-on-surface-variant text-center text-xs">
                          Available only to artwork owner or artist
                        </BodyText>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <Tabs
                onValueChange={(tab) => setTab(tab as 'attributes' | 'details' | 'provenance' | 'offers')}
                className="flex flex-1 flex-col"
              >
                <TabsList className="px-sm gap-md border-outline-variant flex overflow-x-auto border-b [justify-content:safe_center] md:overflow-x-hidden">
                  <TabsTrigger
                    value="attributes"
                    currentTab={tab}
                    onClick={() => {
                      trackSelectionMade(TrackedSelectionNames.tokenPageTab, {
                        selection: 'attributes',
                        options: ['attributes', 'details', 'provenance', 'offers']
                      })
                    }}
                  >
                    Attributes
                  </TabsTrigger>
                  <TabsTrigger
                    value="details"
                    currentTab={tab}
                    onClick={() =>
                      trackSelectionMade(TrackedSelectionNames.tokenPageTab, {
                        selection: 'details',
                        options: ['attributes', 'details', 'provenance', 'offers']
                      })
                    }
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="provenance"
                    currentTab={tab}
                    onClick={() =>
                      trackSelectionMade(TrackedSelectionNames.tokenPageTab, {
                        selection: 'provenance',
                        options: ['attributes', 'details', 'provenance', 'offers']
                      })
                    }
                  >
                    Provenance
                  </TabsTrigger>
                  <TabsTrigger
                    value="offers"
                    currentTab={tab}
                    onClick={() => {
                      trackSelectionMade(TrackedSelectionNames.tokenPageTab, {
                        selection: 'offers',
                        options: ['attributes', 'details', 'provenance', 'offers']
                      })
                    }}
                  >
                    Offers
                  </TabsTrigger>
                </TabsList>
                <div className="bg-surface-low relative flex-1">
                  <div className="md:absolute md:inset-0 md:overflow-auto">
                    <AnimatePresence mode="wait" propagate>
                      <TabContent value={tab} key={tab} className="h-full">
                        {renderTabContent()}
                      </TabContent>
                    </AnimatePresence>
                  </div>
                </div>
              </Tabs>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Token display */}
        <motion.div
          className={cn(
            `${!isCustomizing ? 'bg-surface-container' : 'bg-surface-container-inverted'}`,
            'pr-md py-sm relative h-[70vh] [--info-closed-padding-left:24px] [--info-open-padding-left:24px] md:h-full md:w-full md:[--info-open-padding-left:524px]'
          )}
          initial={false}
          animate={{
            paddingLeft: infoOpen ? 'var(--info-open-padding-left)' : 'var(--info-closed-padding-left)',
            transition: { visualDuration: 0.4, bounce: 0 }
          }}
        >
          <div className="relative h-full w-full">
            <div className="gap-sm absolute inset-0 flex flex-col">
              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setQueryStates({ infoOpen: !infoOpen })
                  }}
                  className="hidden md:block"
                >
                  <ArrowLineLeftIcon
                    className={cn('w-sm h-sm fill-on-surface-variant transition-transform duration-300', {
                      'rotate-180': !infoOpen
                    })}
                  />
                </button>
                {isCustomizing ? null : (
                  <div className="gap-sm flex flex-1 items-center justify-between md:flex-none md:justify-start">
                    {!galleryFrame ? (
                      <>
                        <div className="gap-sm flex">
                          <AllPurposeLink
                            href={tokenExportUrl}
                            target="_blank"
                            onClick={() => {
                              trackButtonClicked(TrackedButtonNames.exportToken, {
                                token,
                                customProperties: {
                                  location: 'Token Page'
                                }
                              })
                            }}
                          >
                            <ExportIcon className="w-sm h-sm fill-on-surface-variant" />
                          </AllPurposeLink>
                          {token.media_url ? (
                            <a
                              href={token.media_url ?? ''}
                              target="_blank"
                              onClick={() => {
                                trackButtonClicked(TrackedButtonNames.viewImage, {
                                  token,
                                  customProperties: {
                                    location: 'Token Page'
                                  }
                                })
                              }}
                            >
                              <ImageIcon className="w-sm h-sm fill-on-surface-variant" />
                            </a>
                          ) : null}
                          {token.live_view_url ? (
                            <a
                              href={token.live_view_url ?? ''}
                              target="_blank"
                              onClick={() => {
                                trackButtonClicked(TrackedButtonNames.openLiveView, {
                                  token,
                                  customProperties: {
                                    location: 'Token Page'
                                  }
                                })
                              }}
                            >
                              <ArrowsOutIcon className="w-sm h-sm fill-on-surface-variant" />
                            </a>
                          ) : null}
                        </div>
                        <div className="bg-on-surface-variant hidden h-[18px] w-[1px] md:block" />
                      </>
                    ) : null}
                    <div className="gap-sm flex">
                      {prevTokenUrl && !hideNavigationArrows ? (
                        <OptionalInterceptLink
                          href={prevTokenUrl}
                          replace={!modal ? undefined : true}
                          skipIntercept={!modal}
                          onClick={() => {
                            trackButtonClicked(TrackedButtonNames.paginateLeft, {
                              token,
                              customProperties: {
                                location: 'Token Page'
                              }
                            })
                          }}
                        >
                          <ArrowRightIcon className="w-sm h-sm fill-on-surface-variant rotate-180" />
                        </OptionalInterceptLink>
                      ) : (
                        <ArrowRightIcon className="w-sm h-sm fill-outline-variant rotate-180" />
                      )}
                      {nextTokenUrl && !hideNavigationArrows ? (
                        <OptionalInterceptLink
                          href={nextTokenUrl}
                          replace={!modal ? undefined : true}
                          skipIntercept={!modal}
                          onClick={() => {
                            trackButtonClicked(TrackedButtonNames.paginateRight, {
                              token,
                              customProperties: {
                                location: 'Token Page'
                              }
                            })
                          }}
                        >
                          <ArrowRightIcon className="w-sm h-sm fill-on-surface-variant" />
                        </OptionalInterceptLink>
                      ) : (
                        <ArrowRightIcon className="w-sm h-sm fill-outline-variant" />
                      )}
                      {modal ? (
                        <Close
                          onClick={() => {
                            trackButtonClicked(TrackedButtonNames.closeTokenPage, {
                              token,
                              customProperties: {
                                location: 'Token Page'
                              }
                            })
                          }}
                        >
                          <CloseIcon className="w-sm h-sm fill-on-surface-variant" />
                        </Close>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative flex-1">
                <div className="absolute inset-0">
                  {galleryFrame ? (
                    <div className="relative flex h-full w-full items-center justify-center">
                      <div className="relative aspect-[1.6] w-full">
                        <Image className="w-full" src={`/gallery.webp`} alt="gallery" fill />
                        <div className="absolute left-[28%] top-[36%] flex h-[40%] w-[40%] flex-col items-center justify-center">
                          <TokenDisplay
                            token={token}
                            project={project}
                            reloadOnWindowResizeOnly
                            preferredDisplayType={preferredDisplayType}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <TokenDisplay
                      token={{
                        ...token,
                        live_view_url: token.live_view_url
                          ? `${token.live_view_url}${
                              isCustomizing &&
                              (Object.keys(customizationParams).length || Object.keys(supplementalParams).length)
                                ? `/web3call-preview${token.live_view_url.includes('?') ? '&' : '?'}${new URLSearchParams({ ...customizationParams, ...supplementalParams })}`
                                : ''
                            }`
                          : token.live_view_url
                      }}
                      project={project}
                      reloadOnWindowResizeOnly
                      preferredDisplayType={preferredDisplayType}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

function TabsTrigger({ value, children, currentTab, className, ...props }: TabsTriggerProps & { currentTab: string }) {
  return (
    <RadixTabsTrigger value={value} className={cn('px-xs pb-2xs text-body relative', className)} {...props}>
      {children}
      {value === currentTab ? (
        <motion.span className="bg-on-surface absolute bottom-0 left-0 right-0 h-[2px]" layoutId="tabs-indicator" />
      ) : null}
    </RadixTabsTrigger>
  )
}

function TabContent({ value, children, className, ...props }: TabsContentProps) {
  return (
    <RadixTabsContent value={value} className={cn(className)} {...props} asChild forceMount>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {children}
      </motion.div>
    </RadixTabsContent>
  )
}
